import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Container, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Divider } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../assets/logo.png';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const navItems = ['Home', 'About', 'Courses', 'Schedule', 'Contact', 'Shop'];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, fontWeight: 'bold', color: 'primary.main' }}>
        Early Education Nook
      </Typography>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item} disablePadding>
            <ListItemButton component={Link} to={item === 'Home' ? '/' : `/${item.toLowerCase()}`} sx={{ textAlign: 'center' }}>
              <ListItemText primary={item} />
            </ListItemButton>
          </ListItem>
        ))}
        {user ? (
          <>
            <Divider sx={{ my: 1 }} />
            {user.role === 'admin' && (
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/admin" sx={{ textAlign: 'center' }}>
                  <ListItemText primary="Admin Dashboard" primaryTypographyProps={{ color: 'secondary', fontWeight: 'bold' }} />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/profile" sx={{ textAlign: 'center' }}>
                <ListItemText primary="Profile" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout} sx={{ textAlign: 'center' }}>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/login" sx={{ textAlign: 'center', justifyContent: 'center' }}>
              <Button variant="contained" fullWidth sx={{ borderRadius: 20 }}>Login / Register</Button>
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      // ... same AppBar props
      elevation={scrolled ? 4 : 0}
      sx={{
        bgcolor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        color: 'primary.main',
        transition: 'all 0.3s ease',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.05)' : 'none'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          {/* Mobile Menu Icon */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              textDecoration: 'none',
              color: 'inherit',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Early Education Nook"
              sx={{
                height: { xs: 32, md: 60 },
                mr: { xs: 1, md: 2 },
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '1rem', md: '1.5rem' },
                lineHeight: { xs: 1.2, md: 1.5 }
              }}
            >
              Early Education Nook
            </Typography>
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
            {navItems.map((item) => (
              <Button
                key={item}
                component={Link}
                to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                color="inherit"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': { color: 'secondary.main', bgcolor: 'transparent' }
                }}
              >
                {item}
              </Button>
            ))}

            {user ? (
              <>
                {user.role === 'admin' && (
                  <Button component={Link} to="/admin" color="secondary" sx={{ fontWeight: 'bold' }}>
                    Admin
                  </Button>
                )}
                <Button component={Link} to="/profile" color="inherit" sx={{ fontWeight: 600 }}>
                  {user.name.split(' ')[0]}
                </Button>
                <Button onClick={handleLogout} variant="outlined" color="primary" sx={{ borderRadius: 20 }}>
                  Logout
                </Button>
              </>
            ) : (
              <Button
                component={Link}
                to="/login"
                variant="contained"
                color="primary"
                sx={{
                  ml: 2,
                  px: 3,
                  boxShadow: '0 4px 14px 0 rgba(74, 20, 140, 0.39)',
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 700
                }}
              >
                Login / Register
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </AppBar>
  );
};

export default Navigation;
